/**
 * 台面宽度限制（与 drawOverSizeArea 一致）：
 * - 未开 xyReverse：按 X 限制（含「右」台面在右，否则在左）
 * - 开启 xyReverse：按 Y 限制（左下角/右下角台面在下，否则在上）
 */
import store from '@/store'

import { getSawKnife } from './LayoutFuncs'
import { getPlateKnifeDiameter } from './dealPaibanData'

export type CounterAxis = 'x' | 'y'

export type CounterAxisRange = {
  axis: CounterAxis
  min: number
  max: number
}

export type PlankSize = {
  plankWidth: number
  plankHeight: number
  bigpart?: any
}

type CounterNcSetting = {
  counter_limit_able?: boolean
  counter_width?: number
  startPosition?: string
  xyReverse?: boolean
}

/** xyReverse 时，台面有效区在下方的工位起点 */
const BOTTOM_START_POSITIONS = ['左下角', '右下角'] as const

/**
 * @description 获取台面有效区间；未开启或不需限制时返回 null
 */
export function getCounterValidRange(
  plankSize: PlankSize,
  ncSetting?: CounterNcSetting | null
): CounterAxisRange | null {
  if (!ncSetting) return null
  const {
    counter_limit_able,
    counter_width,
    startPosition = '',
    xyReverse,
  } = ncSetting
  if (!counter_limit_able || !counter_width) return null

  const plankWidth = Number(plankSize?.plankWidth) || 0
  const plankHeight = Number(plankSize?.plankHeight) || 0

  if (xyReverse) {
    if (!plankHeight || plankHeight <= counter_width) return null
    // 左下角/右下角：台面在下；左上角/右上角：台面在上
    const isBottom = BOTTOM_START_POSITIONS.includes(
      startPosition as typeof BOTTOM_START_POSITIONS[number]
    )
    if (isBottom) {
      return {
        axis: 'y',
        min: plankHeight - counter_width,
        max: plankHeight,
      }
    }
    return { axis: 'y', min: 0, max: counter_width }
  }

  const bigpart = plankSize.bigpart
  if (!bigpart) return null

  const isUseSaw =
    bigpart.isUseSaw ??
    (Array.isArray(bigpart.parts)
      ? bigpart.parts.every((part: { isUseSaw?: boolean }) => part.isUseSaw)
      : false)
  const plankMargin =
    bigpart.surplusInfo && bigpart.surplusInfo.is_plus
      ? isUseSaw
        ? bigpart.margin
        : store.state.selectStandardPlank.plankEdgeOff
      : bigpart.margin

  const marginArr =
    typeof plankMargin === 'string'
      ? plankMargin.split(',').map(Number)
      : Array(4).fill(Number(plankMargin))
  const marginLeft = Number(marginArr[2]) || 0
  const marginRight = Number(marginArr[3]) || 0

  const cutDiameter = Number(getPlateKnifeDiameter(bigpart.stockKey, ncSetting))
  const cutSaw = getSawKnife(ncSetting)

  const knifeR = (isUseSaw ? cutSaw : cutDiameter) / 2

  if (!plankWidth || plankWidth <= counter_width) return null
  // 含「右」：台面在右；否则台面在左
  const isRight = String(startPosition).includes('右')
  if (isRight) {
    return {
      axis: 'x',
      min: plankWidth - counter_width + marginLeft - knifeR,
      max: plankWidth + (marginRight - knifeR),
    }
  }
  return {
    axis: 'x',
    min: marginLeft - knifeR,
    max: counter_width + marginRight - knifeR,
  }
}

/**
 * @description 兼容旧调用：仅取 X 向台面有效区间
 */
export function getCounterValidXRange(
  plankWidth: number,
  ncSetting?: CounterNcSetting | null,
  bigpart?: any
): { minX: number; maxX: number } | null {
  const range = getCounterValidRange(
    { plankWidth, plankHeight: 0, bigpart },
    { ...ncSetting, xyReverse: false }
  )
  if (!range || range.axis !== 'x') return null
  return { minX: range.min, maxX: range.max }
}

/**
 * @description 将矩形边界裁剪到台面有效区间；无交集返回 null
 */
export function clipRectToCounter(
  rect: { left: number; top: number; right: number; bottom: number },
  plankSize: PlankSize,
  ncSetting?: CounterNcSetting | null
): { left: number; top: number; right: number; bottom: number } | null {
  const range = getCounterValidRange(plankSize, ncSetting)
  if (!range) return { ...rect }

  if (range.axis === 'x') {
    const left = Math.max(rect.left, range.min)
    const right = Math.min(rect.right, range.max)
    if (left >= right) return null
    return { ...rect, left, right }
  }

  const top = Math.max(rect.top, range.min)
  const bottom = Math.min(rect.bottom, range.max)
  if (top >= bottom) return null
  return { ...rect, top, bottom }
}

/**
 * @description 兼容旧调用：仅裁剪左右边界
 */
export function clipXRangeToCounter(
  left: number,
  right: number,
  plankWidth: number,
  ncSetting?: CounterNcSetting | null,
  bigpart?: any
): { left: number; right: number } | null {
  const clipped = clipRectToCounter(
    { left, top: 0, right, bottom: 1 },
    { plankWidth, plankHeight: 0, bigpart },
    { ...ncSetting, xyReverse: false }
  )
  if (!clipped) return null
  return { left: clipped.left, right: clipped.right }
}

/**
 * @description 边界框是否超出台面有效宽度
 */
export function isBoundBoxOutsideCounter(
  boundBox: { x1: number; y1?: number; x2: number; y2?: number },
  plankSize: PlankSize | number,
  ncSetting?: CounterNcSetting | null,
  epsilon = 0.01
): boolean {
  const size =
    typeof plankSize === 'number'
      ? { plankWidth: plankSize, plankHeight: 0 }
      : plankSize
  const range = getCounterValidRange(size, ncSetting)
  if (!range) return false

  if (range.axis === 'x') {
    return (
      boundBox.x1 < range.min - epsilon || boundBox.x2 > range.max + epsilon
    )
  }

  const y1 = Number(boundBox.y1)
  const y2 = Number(boundBox.y2)
  if (!Number.isFinite(y1) || !Number.isFinite(y2)) return false
  return y1 < range.min - epsilon || y2 > range.max + epsilon
}

/**
 * @description 点是否在台面有效宽度外
 */
export function isPointOutsideCounter(
  point: { x: number; y?: number } | number,
  plankSize: PlankSize | number,
  ncSetting?: CounterNcSetting | null,
  epsilon = 0.01
): boolean {
  const size =
    typeof plankSize === 'number'
      ? { plankWidth: plankSize, plankHeight: 0 }
      : plankSize
  const range = getCounterValidRange(size, ncSetting)
  if (!range) return false

  if (typeof point === 'number') {
    // 兼容旧调用：仅传 x，且仅在非 xyReverse 时有意义
    if (range.axis !== 'x') return false
    return point < range.min - epsilon || point > range.max + epsilon
  }

  if (range.axis === 'x') {
    return point.x < range.min - epsilon || point.x > range.max + epsilon
  }
  const y = Number(point.y)
  if (!Number.isFinite(y)) return false
  return y < range.min - epsilon || y > range.max + epsilon
}

/**
 * @description 小板沿轴向移动时到台面边界的净空
 * 非 xyReverse：仅 left/right；xyReverse：仅 up/down
 */
export function getCounterAxisClearance(
  direction: 'up' | 'down' | 'left' | 'right',
  activeArr: {
    activeLeft: number
    activeRight: number
    activeTop?: number
    activeBottom?: number
  },
  plankSize: PlankSize | number,
  ncSetting?: CounterNcSetting | null
): number {
  const size =
    typeof plankSize === 'number'
      ? { plankWidth: plankSize, plankHeight: 0 }
      : plankSize
  const range = getCounterValidRange(size, ncSetting)
  if (!range) return Infinity

  if (range.axis === 'x') {
    if (direction === 'left') return activeArr.activeLeft - range.min
    if (direction === 'right') return range.max - activeArr.activeRight
    return Infinity
  }

  const top = Number(activeArr.activeTop)
  const bottom = Number(activeArr.activeBottom)
  if (!Number.isFinite(top) || !Number.isFinite(bottom)) return Infinity
  if (direction === 'up') return top - range.min
  if (direction === 'down') return range.max - bottom
  return Infinity
}
